import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TopBar } from '../components/layout/TopBar';
import { useAppStore } from '../store/useStore';
import { reservationsAPI, analyticsAPI, tablesAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { formatTime, cn } from '../lib/utils';
import { 
  CalendarClock, 
  Users, 
  Percent, 
  AlertTriangle,
  Clock,
  User,
  Phone,
  ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function Dashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedDate, setReservations, reservations, setTables, tables, setAnalytics, analytics } = useAppStore();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [resResponse, analyticsResponse, tablesResponse] = await Promise.all([
        reservationsAPI.list({ date_from: selectedDate, date_to: selectedDate }),
        analyticsAPI.overview({ date_from: selectedDate, date_to: selectedDate }),
        tablesAPI.list(),
      ]);
      setReservations(resResponse.data);
      setAnalytics(analyticsResponse.data);
      setTables(tablesResponse.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const todayReservations = reservations.filter(r => r.date === selectedDate && r.status !== 'cancelled');
  const upcomingReservations = todayReservations
    .filter(r => ['pending', 'confirmed'].includes(r.status))
    .sort((a, b) => a.time.localeCompare(b.time))
    .slice(0, 5);

  const stats = [
    {
      label: t('dashboard.todayReservations'),
      value: analytics?.total_reservations || 0,
      icon: CalendarClock,
      color: 'text-primary',
    },
    {
      label: t('dashboard.totalCovers'),
      value: analytics?.total_covers || 0,
      icon: Users,
      color: 'text-blue-500',
    },
    {
      label: t('dashboard.occupancy'),
      value: `${analytics?.occupancy_rate || 0}%`,
      icon: Percent,
      color: 'text-green-500',
    },
    {
      label: t('dashboard.noShows'),
      value: analytics?.no_shows || 0,
      icon: AlertTriangle,
      color: 'text-amber-500',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      <TopBar title={t('dashboard.title')} helpSection="dashboard" />
      
      <div className="p-6 space-y-6 fade-in">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat, index) => (
            <Card key={index} className="hover-lift" data-testid={`stat-card-${index}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wider">{stat.label}</p>
                    <p className="text-2xl font-semibold mt-1">{stat.value}</p>
                  </div>
                  <div className={cn("p-2 rounded-lg bg-muted", stat.color)}>
                    <stat.icon className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upcoming Reservations */}
          <Card data-testid="upcoming-reservations-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium">{t('dashboard.upcomingReservations')}</CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/reservations')}
                  data-testid="view-all-reservations-btn"
                >
                  Ver todas <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              {upcomingReservations.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {t('reservations.noReservations')}
                </p>
              ) : (
                upcomingReservations.map((res) => (
                  <div
                    key={res.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    data-testid={`reservation-item-${res.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex flex-col items-center justify-center w-12 h-12 rounded-md bg-primary/10 text-primary">
                        <Clock className="h-4 w-4" />
                        <span className="text-xs font-medium mt-0.5">{res.time}</span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <User className="h-3 w-3 text-muted-foreground" />
                          <span className="font-medium text-sm">{res.guest_name}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Users className="h-3 w-3 text-muted-foreground" />
                          <span className="text-xs text-muted-foreground">{res.party_size} {t('common.people')}</span>
                          {res.guest_phone && (
                            <>
                              <Phone className="h-3 w-3 text-muted-foreground ml-2" />
                              <span className="text-xs text-muted-foreground">{res.guest_phone}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <Badge variant="outline" className={`status-${res.status}`}>
                      {t(`status.${res.status}`)}
                    </Badge>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Table Status Overview */}
          <Card data-testid="table-status-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base font-medium">Estado de Mesas</CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/floor-plan')}
                  data-testid="view-floor-plan-btn"
                >
                  Ver plano <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {tables.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground mb-3">No hay mesas configuradas</p>
                  <Button size="sm" onClick={() => navigate('/floor-plan')} data-testid="add-tables-btn">
                    Agregar mesas
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                  {tables.slice(0, 12).map((table) => (
                    <div
                      key={table.id}
                      className={cn(
                        "aspect-square rounded-lg flex flex-col items-center justify-center text-xs font-medium border",
                        table.status === 'available' && "bg-green-500/10 border-green-500/30 text-green-600 dark:text-green-400",
                        table.status === 'reserved' && "bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400",
                        table.status === 'seated' && "bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400",
                        table.status === 'finished' && "bg-teal-500/10 border-teal-500/30 text-teal-600 dark:text-teal-400",
                        table.status === 'blocked' && "bg-slate-500/10 border-slate-500/30 text-slate-600 dark:text-slate-400"
                      )}
                      data-testid={`table-status-${table.id}`}
                    >
                      <span className="text-sm font-semibold">{table.number}</span>
                      <span className="text-[10px] opacity-80">{table.capacity_max}p</span>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Legend */}
              <div className="flex flex-wrap gap-3 mt-4 pt-4 border-t border-border">
                {['available', 'reserved', 'seated', 'blocked'].map((status) => (
                  <div key={status} className="flex items-center gap-1.5 text-xs">
                    <div className={cn(
                      "w-2.5 h-2.5 rounded-full",
                      status === 'available' && "bg-green-500",
                      status === 'reserved' && "bg-amber-500",
                      status === 'seated' && "bg-blue-500",
                      status === 'blocked' && "bg-slate-500"
                    )} />
                    <span className="text-muted-foreground">{t(`status.${status}`)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
